import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, Form, Input, Text, TextButton, Title, toastService } from '@thefarmersfront/kitchen';

import { BrandLogo, InfoText, InputLabel } from '~/components/common';

import { usePostUserFindId } from '~/hooks/auth';
import { useLoadingStore } from '~/store';
import { validateAlphabetNumericOnly, validateAuthOwnerName, validateEmailFormat, validateNoSpace } from '~/utils';

import { FIND_ID, FIND_ID_INFO_TEXT, PATH } from '~/constants';

import type { TypeErrorResponseData, TypeInputState, TypeInputStatus } from '~/types/common.types';

import styles from './FindIdPage.module.scss';

const FindIdPage = () => {
  const { postUserFindId } = usePostUserFindId();
  const { setIsLoading } = useLoadingStore();

  const [ownerCode, setOwnerCode] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');

  const [ownerCodeInputStatus, setOwnerCodeInputStatus] = useState<TypeInputState>({
    status: 'default',
  });
  const [ownerNameInputStatus, setOwnerNameInputStatus] = useState<TypeInputState>({
    status: 'default',
  });
  const [ownerEmailInputStatus, setOwnerEmailInputStatus] = useState<TypeInputState>({
    status: 'default',
  });

  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setAllFieldsStatusAtOnce('default');

    const isAllFieldsFilled = ownerCode && ownerName && ownerEmail;

    if (!isAllFieldsFilled) {
      validateEmptyFields();

      return;
    }

    const isOwnerCodeValid = validateNoSpace(ownerCode) && validateAlphabetNumericOnly(ownerCode);
    const isOwnerNameValid = validateNoSpace(ownerName) && validateAuthOwnerName(ownerName);
    const isOwnerEmailValid = validateNoSpace(ownerEmail) && validateEmailFormat(ownerEmail);

    if (!(isOwnerCodeValid && isOwnerNameValid && isOwnerEmailValid)) {
      toastService.addToast({
        message: '화주사코드 또는 담당자명 또는 이메일을\n확인해 주세요.',
        type: 'error',
      });

      setAllFieldsStatusAtOnce('error');

      return;
    }

    postFindId();
  };

  const postFindId = async () => {
    try {
      setIsLoading(true);

      await postUserFindId({
        ownerCode,
        name: ownerName,
        email: ownerEmail,
      });

      toastService.addToast({
        message: '성공적으로 이메일이 발송되었습니다.',
      });
      navigate(PATH.LOGIN);
    } catch (error) {
      const errorCode = (error as TypeErrorResponseData['error']).code;

      if (errorCode !== FIND_ID.FAILED_TO_SEND_EMAIL) {
        setAllFieldsStatusAtOnce('error');
      }

      toastService.addToast({
        message: (error as TypeErrorResponseData['error']).message,
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmptyFields = () => {
    if (!ownerCode) {
      setOwnerCodeInputStatus({
        status: 'error',
        message: '화주사코드를 입력해 주세요',
      });
    }

    if (!ownerName) {
      setOwnerNameInputStatus({
        status: 'error',
        message: '담당자명을 입력해 주세요',
      });
    }

    if (!ownerEmail) {
      setOwnerEmailInputStatus({
        status: 'error',
        message: '이메일을 입력해 주세요',
      });
    }
  };

  const setAllFieldsStatusAtOnce = (status: TypeInputStatus) => {
    setOwnerCodeInputStatus({ status });
    setOwnerNameInputStatus({ status });
    setOwnerEmailInputStatus({ status });
  };

  return (
    <div className={styles['find-id-page-container']}>
      <div className={styles['find-id-page-wrapper']}>
        <BrandLogo width={147} height={19} className={styles['find-id-logo']} />
        <Title size="accessibility1">아이디 찾기</Title>
        <Form className={styles['find-id-form']} onSubmit={handleSubmit}>
          <InputLabel htmlFor="owner-code" name="화주사코드">
            <Input
              id="owner-code"
              type="text"
              placeholder="화주사코드를 입력해 주세요."
              status={ownerCodeInputStatus.status}
              validationMessage={ownerCodeInputStatus.message ?? ''}
              onChange={(e) => setOwnerCode(e.target.value)}
              onFocus={() => setOwnerCodeInputStatus({ status: 'default' })}
            />
          </InputLabel>
          <InputLabel htmlFor="owner-name" name="담당자명">
            <Input
              id="owner-name"
              type="text"
              placeholder="담당자명을 입력해 주세요."
              status={ownerNameInputStatus.status}
              validationMessage={ownerNameInputStatus.message ?? ''}
              onChange={(e) => setOwnerName(e.target.value)}
              onFocus={() => setOwnerNameInputStatus({ status: 'default' })}
            />
          </InputLabel>
          <InputLabel htmlFor="owner-email" name="이메일">
            <Input
              id="owner-email"
              type="text"
              placeholder="이메일을 입력해 주세요."
              status={ownerEmailInputStatus.status}
              validationMessage={ownerEmailInputStatus.message ?? ''}
              onChange={(e) => setOwnerEmail(e.target.value)}
              onFocus={() => setOwnerEmailInputStatus({ status: 'default' })}
            />
          </InputLabel>
          <div className={styles['find-id-form-info']}>
            {FIND_ID_INFO_TEXT.map((text) => (
              <InfoText key={text}>{text}</InfoText>
            ))}
          </div>
          <div className={styles['button-container']}>
            <Button color="tertiary" variant="outlined" onClick={() => navigate(PATH.LOGIN)}>
              이전으로
            </Button>
            <Button color="secondary" type="submit">
              이메일 전송
            </Button>
          </div>
        </Form>
        <div className={styles['find-password-guide']}>
          <Text color="secondary" size="medium">
            비밀번호가 기억나지 않으신가요?
          </Text>
          <TextButton href={PATH.FIND_PASSWORD} size="tiny">
            비밀번호 찾기
          </TextButton>
        </div>
      </div>
    </div>
  );
};

export default FindIdPage;
